import { useState } from 'react';
//api
import { getPOIsByGroupAPI } from '../services/poiGroupsAPI';
import { getPOIsAPI } from '../services/poiAPI';
//context
import { useLoading } from '../contexts/LoadingContext';
import { useError } from '../contexts/ErrorContext';

/**
 * 
 * Gets pois by job or poi group and creates options for select components
 * 
 * @returns {function} fetchPoisByGroup - calls the API to get all the pois in requested group
 * @returns {Object[]} poiNameOptions - options for select components, with poi id and name.  Looks like: {value: 1, label: "POI_1"}
 * @returns {Object[]} poisInGroup - array of pois in the group
 * @returns {Object[]} poisInGroupOriginal - copy of the pois originally fetched from the api, so we can compare which pois have been added/removed from the group
 * @returns {function} setPoisInGroup - setter function to set pois selected for a group
 * @returns {function} setPoisInGroupOriginal - setter function to set the pois originally in group 
 *  
 * @category Hooks - Dropdown Options
 */
const useFetchPoiOptions = () => {
    const [poiNameOptions, setPoiNameOptions] = useState([])
    const [poisInGroup, setPoisInGroup] = useState([])
    const [poisInGroupOriginal, setPoisInGroupOriginal] = useState([])
    
    const { showLoading, hideLoading } = useLoading();
    const { showError } = useError();
    
    const fetchPoiOptionsByGroup = async (poiGroupId) => {
        showLoading('Getting POIs...');
        try {
            const response = await getPOIsByGroupAPI(poiGroupId);

            if (!response.success) {
                throw new Error(response.message);
            }

            const pois = response.data.pois;

            let poiOptions = []
                
            if (pois.length > 0) {
                poiOptions = pois.map(poi => ({
                    value: poi.POIID,
                    label: poi.POI_Name,
                    linkedPOIs: poi.linkedPOIs ?? [], //if we need any more poi data in a group context than just linkedPOIs, consider re-working this to have a separate 'pois' state
                    description: poi.Description
                }));
                
            }

            setPoisInGroup(poiOptions)
            setPoisInGroupOriginal(poiOptions)

        } catch (error) {
            showError(`Error getting pois - ${error.message}`);
        } finally {
            hideLoading();
        }
    };

    //get all pois for job to fill in possible poi options in dropdown
    const fetchPoiOptionsByJob = async (JobID) => {
        showLoading('Getting POIs...');
        try {
            const response = await getPOIsAPI(JobID);

            if (!response.success) {
                throw new Error(response.message);
            }

            const pois = response.data;
                
            if (pois.length > 0) {
                const poiOptions = pois.map(poi => ({
                    value: poi.POIID,
                    label: poi.POI_Name,
                    linkedPOIs: poi.linkedPOIs ?? [],
                    description: poi.Description
                }));
                
                setPoiNameOptions(poiOptions)
            }

        } catch (error) {
            showError(`Error getting pois - ${error.message}`);
        } finally {
            hideLoading();
        }
    };

    return {
        fetchPoiOptionsByJob, 
        fetchPoiOptionsByGroup,
        poiNameOptions,
        poisInGroup,
        poisInGroupOriginal,
        setPoisInGroup,
        setPoisInGroupOriginal
     };
};

export default useFetchPoiOptions;